import { Add as AddIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Pagination,
  Snackbar,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import TicketQuickPreviewDialog from "../components/search/TicketQuickPreviewDialog";
import TicketSearchCard from "../components/search/TicketSearchCard";
import TicketSearchFilters from "../components/search/TicketSearchFilters";
import { ticketsAPI } from "../services/api";
import { loadAllReferenceData } from "../store/slices/referenceDataSlice";
import {
  fetchUserSearchOptions,
  searchTickets,
} from "../store/slices/ticketsSlice";
import { buildFilterParams, emptySearchFilters } from "../utils/buildSearchParams";
import { useHasPermission } from "../hooks/useHasPermission";
import { PERMISSIONS } from "../utils/permissions";

const PAGE_SIZE = 20;

const TicketsListPage = ({ onNavigate }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const isRTL = i18n.language === "ar";
  const canCreateTicket = useHasPermission(PERMISSIONS.CREATE_TICKETS);
  const canExportTickets = useHasPermission(PERMISSIONS.EXPORT_TICKETS);

  const {
    searchResults,
    searchPagination,
    searchLoading,
    searchError,
    userSearchOptions,
  } = useSelector((state) => state.tickets);

  const referenceData = useSelector((state) => state.referenceData);

  const [filters, setFilters] = useState(emptySearchFilters);
  const [previewTicket, setPreviewTicket] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState("");

  const runSearch = useCallback(
    (page = 1) => {
      dispatch(
        searchTickets({
          page,
          pageSize: PAGE_SIZE,
          filters,
        })
      );
    },
    [dispatch, filters]
  );

  useEffect(() => {
    dispatch(loadAllReferenceData());
    dispatch(fetchUserSearchOptions());
    dispatch(
      searchTickets({
        page: 1,
        pageSize: PAGE_SIZE,
        filters: emptySearchFilters(),
      })
    );
  }, [dispatch]);

  const handleSearch = () => {
    runSearch(1);
  };

  const handleClear = () => {
    const empty = emptySearchFilters();
    setFilters(empty);
    dispatch(
      searchTickets({
        page: 1,
        pageSize: PAGE_SIZE,
        filters: empty,
      })
    );
  };

  const handlePageChange = (_, page) => {
    runSearch(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleExport = async () => {
    setIsExporting(true);
    setExportError("");
    try {
      await ticketsAPI.exportTicketsExcel({
        ...buildFilterParams(filters),
        lang: i18n.language,
      });
    } catch (err) {
      setExportError(err?.message || t("search.exportFailed"));
    } finally {
      setIsExporting(false);
    }
  };

  const { currentPage, totalPages, totalCount, pageSize } = searchPagination;
  const from = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const to = Math.min(currentPage * pageSize, totalCount);

  return (
    <Container dir={isRTL ? "rtl" : "ltr"} maxWidth="xl" sx={{ mt: 1, mb: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: "bold",
            textAlign: { xs: "center", sm: isRTL ? "right" : "left" },
          }}
        >
          {t("search.title")}
        </Typography>
        {canCreateTicket && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => onNavigate && onNavigate("/create-ticket")}
          >
            {t("tickets.createNew")}
          </Button>
        )}
      </Box>

      <TicketSearchFilters
        filters={filters}
        onChange={setFilters}
        onSearch={handleSearch}
        onClear={handleClear}
        isRTL={isRTL}
        referenceData={referenceData}
        userOptions={userSearchOptions}
        canExport={canExportTickets}
        isExporting={isExporting}
        onExport={handleExport}
      />

      {searchError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {searchError}
        </Alert>
      )}

      {totalCount > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("search.resultsCount", { from, to, total: totalCount })}
        </Typography>
      )}

      {searchLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : searchResults.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {searchError ? t("home.errors.fetchError") : t("search.noResults")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchError || t("search.noResultsDescription")}
          </Typography>
          {searchError && (
            <Button variant="contained" sx={{ mt: 2 }} onClick={() => runSearch(1)}>
              {t("common.refresh")}
            </Button>
          )}
        </Box>
      ) : (
        <>
          {searchResults.map((ticket) => (
            <TicketSearchCard
              key={ticket.ticket_id}
              ticket={ticket}
              isRTL={isRTL}
              onNavigate={onNavigate}
              onQuickPreview={setPreviewTicket}
            />
          ))}

          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}

      <TicketQuickPreviewDialog
        open={Boolean(previewTicket)}
        ticket={previewTicket}
        onClose={() => setPreviewTicket(null)}
        isRTL={isRTL}
      />

      <Snackbar
        open={Boolean(exportError)}
        autoHideDuration={6000}
        onClose={() => setExportError("")}
      >
        <Alert
          onClose={() => setExportError("")}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {exportError}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default TicketsListPage;
